/**
 * ViewPresetsIsland - Saved camera view presets for the ramp viewport
 *
 * Compact popover in bottom-right showing named camera positions.
 * Click a preset to fly to it. Save current view, set default, delete.
 * The default preset auto-loads when entering the ramp view.
 */

import { useState, useRef, useEffect } from 'react'
import { Camera, ChevronDown, Star, MoreHorizontal, Plus, Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Island } from './Island'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  useViewPresets,
  useCreateViewPreset,
  useDeleteViewPreset,
  useSetDefaultPreset,
  useUnsetDefaultPreset,
} from '@/hooks/useViewPresets'
import type { ViewPresetRow } from '@/lib/api/view-presets'

interface ViewPresetsIslandProps {
  locationId: string
  map: mapboxgl.Map | null | undefined
  minimized?: boolean
}

export function ViewPresetsIsland({ locationId, map, minimized = false }: ViewPresetsIslandProps) {
  const [open, setOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [newName, setNewName] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const { data: presets = [] } = useViewPresets(locationId)
  const createMutation = useCreateViewPreset()
  const deleteMutation = useDeleteViewPreset()
  const setDefaultMutation = useSetDefaultPreset()
  const unsetDefaultMutation = useUnsetDefaultPreset()

  const defaultPreset = presets.find((p) => p.is_default)
  const activeLabel = defaultPreset?.name || 'Views'

  // Focus input when save mode activates
  useEffect(() => {
    if (isSaving) {
      // Small delay to let the input render
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [isSaving])

  const handleFlyTo = (preset: ViewPresetRow) => {
    if (!map) return
    map.flyTo({
      center: [preset.center_lng, preset.center_lat],
      zoom: preset.zoom,
      pitch: preset.pitch,
      bearing: preset.bearing,
      duration: 1500,
    })
    setOpen(false)
  }

  const handleSaveCurrentView = () => {
    if (!map || !newName.trim()) return

    const center = map.getCenter()
    createMutation.mutate(
      {
        locationId,
        name: newName.trim(),
        center_lng: center.lng,
        center_lat: center.lat,
        zoom: map.getZoom(),
        pitch: map.getPitch(),
        bearing: map.getBearing(),
      },
      {
        onSuccess: () => {
          setNewName('')
          setIsSaving(false)
        },
      }
    )
  }

  const handleToggleDefault = (preset: ViewPresetRow) => {
    if (preset.is_default) {
      unsetDefaultMutation.mutate({ presetId: preset.id, locationId })
    } else {
      setDefaultMutation.mutate({ presetId: preset.id, locationId })
    }
  }

  const handleDelete = (preset: ViewPresetRow) => {
    deleteMutation.mutate({ id: preset.id, locationId })
  }

  const handleSaveKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveCurrentView()
    } else if (e.key === 'Escape') {
      setIsSaving(false)
      setNewName('')
    }
  }

  return (
    <div className={cn(
      'absolute bottom-14 right-4 z-20',
      'transition-all duration-200 ease-out',
      minimized && 'opacity-0 scale-95 pointer-events-none'
    )}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div>
            <Island className="px-2 py-1.5 cursor-pointer hover:bg-background/95">
              <div className="flex items-center gap-1.5 text-xs">
                <Camera className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="max-w-[80px] truncate">{activeLabel}</span>
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </div>
            </Island>
          </div>
        </PopoverTrigger>

        <PopoverContent
          side="top"
          align="end"
          className="w-64 p-0"
          sideOffset={8}
        >
          {/* Preset list */}
          <div className="max-h-48 overflow-y-auto">
            {presets.length === 0 && (
              <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                No saved views yet
              </div>
            )}
            {presets.map((preset) => (
              <div
                key={preset.id}
                className="flex items-center gap-1.5 px-2 py-1.5 hover:bg-accent/50 group"
              >
                {/* Star toggle */}
                <button
                  onClick={() => handleToggleDefault(preset)}
                  className="shrink-0 p-0.5 rounded hover:bg-accent"
                  title={preset.is_default ? 'Remove default' : 'Set as default'}
                >
                  <Star
                    className={cn(
                      'h-3.5 w-3.5',
                      preset.is_default
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-muted-foreground/40'
                    )}
                  />
                </button>

                {/* Preset name — click to fly */}
                <button
                  onClick={() => handleFlyTo(preset)}
                  className="flex-1 text-left text-xs truncate hover:underline"
                >
                  {preset.name}
                </button>

                {/* More menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="shrink-0 p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-accent transition-opacity">
                      <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuItem onClick={() => handleToggleDefault(preset)}>
                      <Star className="h-3.5 w-3.5 mr-2" />
                      {preset.is_default ? 'Remove default' : 'Set as default'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDelete(preset)}
                      className="text-destructive focus:text-destructive"
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>

          {/* Separator */}
          <div className="border-t" />

          {/* Save current view */}
          <div className="p-2">
            {isSaving ? (
              <div className="flex items-center gap-1.5">
                <Input
                  ref={inputRef}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={handleSaveKeyDown}
                  placeholder="View name..."
                  className="h-7 text-xs"
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 shrink-0"
                  onClick={handleSaveCurrentView}
                  disabled={!newName.trim() || createMutation.isPending}
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 shrink-0"
                  onClick={() => { setIsSaving(false); setNewName('') }}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs h-7"
                onClick={() => setIsSaving(true)}
                disabled={!map}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Save current view
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
